import { type MouseEvent, type ReactNode } from 'react';

import { COLOR_TOKENS, type ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, type SxProps, type Theme, Tooltip } from '@mui/material';

const RING_GAP_PADDING = 6;
const OUTER_INSET = 2;
const MIN_RING_RADIUS = 1;

export type ConcentricRing = Readonly<{
  id: string;
  pct: number;
  color: ColorToken;
  tooltip?: string;
  onClick?: () => void;
}>;

export type ConcentricRingChartProps = Readonly<{
  /** Ordered innermost ring first. Each ring is an independent gauge, so values need not sum. */
  rings: readonly ConcentricRing[];
  size?: number;
  strokeWidth?: number;
  trackColor?: ColorToken;
  centerContent?: ReactNode;
}>;

/** Nested ring gauges sharing a centre, each showing its own percentage. */
export const ConcentricRingChart = ({
  rings,
  size = 200,
  strokeWidth = 14,
  trackColor = 'Colors/Background/bg-tertiary',
  centerContent,
}: ConcentricRingChartProps) => {
  const center = size / 2;
  const outermost = center - strokeWidth - OUTER_INSET;
  const ringCount = Math.max(rings.length, 1);
  // gap shrinks rather than letting a radius go negative, which SVG silently drops
  const gap = Math.min(
    strokeWidth + RING_GAP_PADDING,
    Math.max((outermost - MIN_RING_RADIUS) / ringCount, 0),
  );
  const innerRadius = Math.max(outermost - (ringCount - 1) * gap, MIN_RING_RADIUS);

  return (
    <Box sx={wrapSx}>
      <Box component="svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {rings.map((ring, index) => {
          const radius = innerRadius + index * gap;
          const circumference = 2 * Math.PI * radius;
          const filled = (Math.min(Math.max(ring.pct, 0), 100) / 100) * circumference;

          const group = (
            <g
              key={ring.id}
              id={ring.onClick && ring.id}
              style={ring.onClick && { cursor: 'pointer' }}
              onClick={
                ring.onClick &&
                ((event: MouseEvent) => {
                  event.stopPropagation();
                  ring.onClick?.();
                })
              }
            >
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={COLOR_TOKENS[trackColor]}
                strokeWidth={strokeWidth}
              />
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={COLOR_TOKENS[ring.color]}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={`${filled} ${circumference - filled}`}
                transform={`rotate(-90 ${center} ${center})`}
              />
            </g>
          );

          return ring.tooltip ? (
            <Tooltip key={ring.id} arrow placement="top" title={ring.tooltip}>
              {group}
            </Tooltip>
          ) : (
            group
          );
        })}
      </Box>
      {centerContent ? <Box sx={centerSx}>{centerContent}</Box> : null}
    </Box>
  );
};

const wrapSx: SxProps<Theme> = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const centerSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
